import { useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { ChildPositionContext } from './ChildPositionProvider';

type UseChildPositionReturn = {
  /** Required ref to be attached to the element to track */
  ref: React.RefObject<HTMLElement | null>;
  index?: number | null;
  length: number;
  isFirstItem: boolean;
  isLastItem: boolean;
};

/**
 * Hook to track the index of the current element within a ChildPositionProvider.
 */
export function useChildPosition(): UseChildPositionReturn {
  const { register, unregister, map } = useContext(ChildPositionContext);

  const ref = useRef<HTMLElement>(null);
  const index = ref.current ? map.get(ref.current) : null;
  const length = map.size;

  const isFirstItem = !index;
  const isLastItem = !length || index === length - 1;

  useLayoutEffect(() => {
    const node = ref.current;
    if (node) {
      register(node);
      return () => {
        unregister(node);
      };
    }
  }, [register, unregister]);

  return useMemo(
    () => ({
      ref,
      index,
      length,
      isFirstItem,
      isLastItem,
    }),
    [index, ref, length, isFirstItem, isLastItem],
  );
}
